// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AddRounded, SearchRounded } from '@/constants/hardfin-standard-icons';
import { SparseItem } from '@/sparse/api/types';
import {
  Autocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ForwardedRef, HTMLAttributes, forwardRef } from 'react';

/**
 * Special ID used to identify the "create new item" option in the dropdown.
 * This distinguishes it from regular item options for special handling.
 */
export const CREATE_ITEM_OPTION_ID = 'create-item';

/**
 * Determines if two item options represent the same item.
 * Uses the item ID for comparison to handle cases where other
 * properties might have changed.
 */
function isOptionEqualToValue(option: SparseItem, value: SparseItem) {
  return option.id === value.id;
}

/**
 * Renders a single option in the autocomplete dropdown.
 * Provides different styling for:
 * - Regular items
 * - The "create new item" option
 */
function renderOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: SparseItem
) {
  if (option.id === CREATE_ITEM_OPTION_ID) {
    return (
      <li {...props} key={option.id}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AddRounded fontSize="small" color="primary" />
          <Typography color="primary.main">
            Add item <b>{option.name}</b>
          </Typography>
        </Stack>
      </li>
    );
  }

  return (
    <li {...props} key={option.id}>
      <Typography>
        {option.sku}
        <Typography component="span" color="text.secondary">
          {' '}
          ({option.name})
        </Typography>
      </Typography>
    </li>
  );
}

/**
 * Extracts the display label from an item option.
 * Handles both string inputs (for free-solo mode) and item objects.
 */
function getOptionLabel<FreeSolo extends boolean | undefined>(
  option: SparseItem | AutocompleteFreeSoloValueMapping<FreeSolo>
) {
  if (typeof option === 'string') {
    return option;
  }

  return `${option.sku} (${option.name})`;
}

/**
 * Props for the BOMItemAutocomplete component.
 * Extends Material-UI's Autocomplete props while adding custom form-related props.
 */
interface Props<Option extends SparseItem, FreeSolo extends boolean | undefined>
  extends Omit<
    AutocompleteProps<Option, false, false, FreeSolo>,
    'renderInput' | 'isOptionEqualToValue' | 'getOptionLabel' | 'renderOption'
  > {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;

  showSearchAdornment?: boolean;
}

/**
 * A reusable autocomplete component for selecting BOM items.
 *
 * Features:
 * - Type-safe item selection
 * - Custom "create new" option rendering
 * - Form integration with validation states
 * - Keyboard navigation support
 * - Ref forwarding for form libraries
 *
 * This component is designed to be used by higher-level components
 * that handle data fetching and state management, such as BOMItemSearch.
 */
function BOMItemAutocompleteRenderer<
  Option extends SparseItem,
  FreeSolo extends boolean | undefined,
>(props: Props<Option, FreeSolo>, ref: ForwardedRef<HTMLInputElement>) {
  const {
    name,
    label,
    placeholder,
    error,
    helperText,
    showSearchAdornment,
    ...autocompleteProps
  } = props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    const { InputProps, inputProps, ...remainingProps } = parameters;
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...remainingProps}
        InputProps={{
          ...InputProps,
          startAdornment: (
            <>
              {showSearchAdornment && (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              )}
              {InputProps?.startAdornment}
            </>
          ),
        }}
        inputProps={inputProps}
      />
    );
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText="No items available"
    />
  );
}

/**
 * A controlled component for selecting BOM items with autocomplete functionality.
 * Wraps the BOMItemAutocompleteRenderer with ref forwarding for form integration.
 */
export const BOMItemAutocomplete = forwardRef(BOMItemAutocompleteRenderer);
